import { Fragment, type ReactNode } from 'react'
import { formatPct, formatRevenueCompact, formatRevenueFull } from './formatters'
import { DiffValue, RowSign, addedRowStyle, publishedValue, removedRowStyle, type RevisionBaseline } from './DiffBits'
import RowCommentButton from './RowCommentButton'
import { type RowAnchor } from './rowAnchor'
import { type CellAnchor } from './cellAnchor'
import CommentableCell, { rowClickProps } from './CommentableCell'

// Month labels for a revision's actual-vs-plan split; null for a plain version.
type PeriodLabels = { quarters: string; actualQuarters: string } | null

export interface PlanNodeFromServer {
  id: string
  name: string
  shortName?: string | null
  revenue: string
  ytdRevenue?: string
  // Actual sales for the plan's actual months. Present only on a revision.
  actualRevenue?: string
  share: number
  growthPct?: number | null
  targetRevenue: number
  children: PlanNodeFromServer[]
}

interface FlatRow {
  id: string
  name: string
  depth: number
  hasChildren: boolean
  pastRevenue: number
  ytdRevenue: number
  actualRevenue: number
  targetRevenue: number
  share: number
  growthPct: number | null
}

export function flattenPlanNodes(nodes: PlanNodeFromServer[], depth = 0): FlatRow[] {
  return nodes.flatMap((n) => [
    {
      id: n.id,
      name: n.shortName?.trim() ? n.shortName : n.name,
      depth,
      hasChildren: (n.children?.length ?? 0) > 0,
      pastRevenue: Number(n.revenue ?? 0),
      ytdRevenue: Number(n.ytdRevenue ?? 0),
      actualRevenue: Number(n.actualRevenue ?? 0),
      targetRevenue: n.targetRevenue,
      share: n.share ?? 0,
      growthPct: n.growthPct ?? null,
    },
    ...flattenPlanNodes(n.children ?? [], depth + 1),
  ])
}

// With `baseline` (a revision under review), GitHub diff styling: a changed
// target shows the new value on a green line over the published value on a
// red line; rows the revision added are green with a + sign; rows it
// removed are listed at the end in red with a − sign.
//
// With `periodLabels` (a quarterly revision) a "Q1-Q3 Actuals" column (the
// months already sold) follows Past Contribution, the target column holds
// the remaining months only ("Q4 Target", with the full year under it) and
// Growth reads on the full year (months already sold + target).
export default function VersionViewOverallSection({ planNodes, currency, periodLabels = null, baseline = null, onRowAction, rowExtras, onCellAction, cellCommentCount, cellSelected }: {
  planNodes: PlanNodeFromServer[]
  currency?: string | null
  periodLabels?: PeriodLabels
  baseline?: RevisionBaseline | null
  onRowAction?: (anchor: RowAnchor) => void
  rowExtras?: (anchor: RowAnchor) => ReactNode
  onCellAction?: (cell: CellAnchor) => void
  cellCommentCount?: (cell: CellAnchor) => number
  cellSelected?: (cell: CellAnchor) => boolean
}) {
  const flat = flattenPlanNodes(planNodes)
  const total = planNodes.reduce((s, n) => s + (n.targetRevenue ?? 0), 0)
  const pastTotal = planNodes.reduce((s, n) => s + Number(n.revenue ?? 0), 0)
  const ytdTotal = planNodes.reduce((s, n) => s + Number(n.ytdRevenue ?? 0), 0)
  const actualTotal = planNodes.reduce((s, n) => s + Number(n.actualRevenue ?? 0), 0)
  const cellClass = 'px-3 py-2 border-b text-right tabular-nums'
  // A revision adds the Actuals column and drops Total YTD (its window starts
  // at the plan period, so it is empty until Q4 begins), so 7 either way.
  // A revision adds Actuals and a Full year column and drops Total YTD.
  const colCount = periodLabels ? 8 : 7
  const versions = baseline?.version_numbers
  const totalFullYear = baseline?.total_full_year ?? (actualTotal + total)
  const summaryTargetCell = (
    <td className="px-3 py-2 text-right tabular-nums font-semibold" style={{ color: 'var(--foreground)' }}>
      {periodLabels
        ? formatRevenueFull(total, currency)
        : <DiffValue was={publishedValue(baseline, (b) => b.total_target)} now={total} format={(v) => formatRevenueFull(v, currency)} />}
    </td>
  )
  // Revision only: the plan's full year, green over the published year under review.
  const summaryFullYearCell = periodLabels && (
    <td className="px-3 py-2 text-right tabular-nums font-semibold" style={{ color: 'var(--foreground)' }}>
      <DiffValue was={publishedValue(baseline, (b) => b.total_target)} now={totalFullYear} format={(v) => formatRevenueCompact(v, currency)} />
    </td>
  )
  return (
    <div className="border rounded-md" style={{ borderColor: 'var(--border)', background: 'var(--background)' }}>
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
            <th className="text-left px-3 py-2 border-b" style={{ borderColor: 'var(--border)' }}>Plan node</th>
            <th className="text-right px-3 py-2 border-b" style={{ width: 200, borderColor: 'var(--border)' }}>Past Revenue</th>
            <th className="text-right px-3 py-2 border-b" style={{ width: 110, borderColor: 'var(--border)' }}>Past Contribution</th>
            {periodLabels && (
              <th className="text-right px-3 py-2 border-b" style={{ width: 200, borderColor: 'var(--border)' }}>{periodLabels.actualQuarters} Actuals</th>
            )}
            <th className="text-right px-3 py-2 border-b" style={{ width: 110, borderColor: 'var(--border)' }}>Growth</th>
            <th className="text-right px-3 py-2 border-b" style={{ width: 200, borderColor: 'var(--border)' }}>
              {periodLabels ? `${periodLabels.quarters} Target` : 'Target revenue'}
            </th>
            {periodLabels && (
              <th className="text-right px-3 py-2 border-b" style={{ width: 140, borderColor: 'var(--border)' }}>
                <div>Full year</div>
                {versions && (
                  <div className="text-[10px] font-normal" style={{ color: 'var(--muted-foreground)' }}>
                    v{versions.now} vs v{versions.was}
                  </div>
                )}
              </th>
            )}
            <th className="text-right px-3 py-2 border-b" style={{ width: 110, borderColor: 'var(--border)' }}>Target Contribution</th>
            {!periodLabels && (
              <th className="text-right px-3 py-2 border-b" style={{ width: 220, borderColor: 'var(--border)' }}>Total YTD</th>
            )}
          </tr>
        </thead>
        <tbody>
          {flat.length === 0 ? (
            <tr>
              <td colSpan={colCount} className="px-3 py-4 text-center text-sm" style={{ color: 'var(--muted-foreground)' }}>
                No plan nodes.
              </td>
            </tr>
          ) : (
            flat.map((n) => {
              // Full-year share; actualRevenue is 0 outside a revision.
              const fullYearTotal = actualTotal + total
              const newMix = fullYearTotal > 0 ? (n.actualRevenue + n.targetRevenue) / fullYearTotal : 0
              const was = baseline?.node_targets[n.id]
              const added = baseline?.added_node_ids.includes(n.id) ?? false
              const anchor: RowAnchor = { section: 'overall', anchor_type: 'TopDownPlanNode', anchor_id: n.id, label: n.name }
              const targetAnchor: CellAnchor = { ...anchor, column_key: 'target', column_label: 'Target' }
              const extra = rowExtras?.(anchor)
              // On a revision the diff reads on the full year: the Q4 number is
              // plain and the Full year column beside it carries the marks.
              const fullYearNow = baseline?.node_full_year?.[n.id] ?? (n.actualRevenue + n.targetRevenue)
              // The row has one commentable number (the target). On the review
              // page the whole row opens its comments; the count and the
              // outline sit on the number carrying the green/red marks.
              const mark = { count: cellCommentCount?.(targetAnchor) ?? 0 }
              const selected = cellSelected?.(targetAnchor) ?? false
              const targetCell = (
                <CommentableCell
                  cell={targetAnchor}
                  selected={selected && !periodLabels}
                  className={cellClass}
                  style={{ borderColor: 'var(--border)', color: 'var(--foreground)' }}
                >
                  {periodLabels
                    ? formatRevenueFull(n.targetRevenue, currency)
                    : <DiffValue was={was} now={n.targetRevenue} format={(v) => formatRevenueFull(v, currency)} mark={mark} />}
                </CommentableCell>
              )
              const fullYearCell = periodLabels && (
                <CommentableCell
                  cell={targetAnchor}
                  selected={selected}
                  className={cellClass}
                  style={{ borderColor: 'var(--border)', color: 'var(--foreground)' }}
                  title={formatRevenueFull(fullYearNow, currency)}
                >
                  <DiffValue was={was} now={fullYearNow} format={(v) => formatRevenueCompact(v, currency)} mark={mark} />
                </CommentableCell>
              )
              const rowClick = rowClickProps(`Comments on ${n.name}`, onCellAction && (() => onCellAction(targetAnchor)))
              return (
                <Fragment key={n.id}>
                  <tr
                    className={`${onRowAction ? 'group' : ''} ${onCellAction ? 'cursor-pointer hover:bg-primary/5' : ''}`}
                    style={added ? addedRowStyle : undefined}
                    {...rowClick}
                  >
                    <td
                      className="relative px-3 py-2 border-b"
                      style={{ borderColor: 'var(--border)', paddingLeft: (onRowAction ? 32 : 12) + n.depth * 16, color: 'var(--foreground)', fontWeight: n.hasChildren ? 500 : 400 }}
                    >
                      {onRowAction && <RowCommentButton label={n.name} onClick={() => onRowAction(anchor)} />}
                      {added && <RowSign kind="added" />}
                      {n.name}
                    </td>
                    <td className="px-3 py-2 border-b text-right tabular-nums" style={{ borderColor: 'var(--border)', color: 'var(--foreground)' }}>
                      {formatRevenueFull(n.pastRevenue, currency)}
                    </td>
                    <td className="px-3 py-2 border-b text-right tabular-nums" style={{ borderColor: 'var(--border)', color: 'var(--foreground)' }}>
                      {formatPct(n.share)}
                    </td>
                    {periodLabels && (
                      <td className={cellClass} style={{ borderColor: 'var(--border)', color: 'var(--foreground)' }}>
                        {formatRevenueFull(n.actualRevenue, currency)}
                      </td>
                    )}
                    <td className="px-3 py-2 border-b text-right tabular-nums" style={{ borderColor: 'var(--border)', color: 'var(--foreground)' }}>
                      {n.growthPct == null ? '—' : `${n.growthPct}%`}
                    </td>
                    {targetCell}
                    {fullYearCell}
                    <td className="px-3 py-2 border-b text-right tabular-nums" style={{ borderColor: 'var(--border)', color: 'var(--foreground)' }}>
                      {formatPct(newMix)}
                    </td>
                    {!periodLabels && (
                      <td className="px-3 py-2 border-b text-right tabular-nums" style={{ borderColor: 'var(--border)', color: 'var(--foreground)' }}>
                        {formatRevenueFull(n.ytdRevenue, currency)}
                        <span style={{ color: 'var(--muted-foreground)' }}>
                          {' · '}{n.targetRevenue > 0 ? formatPct(n.ytdRevenue / n.targetRevenue) : '—'}
                        </span>
                      </td>
                    )}
                  </tr>
                  {extra && (
                    <tr>
                      <td colSpan={colCount} className="p-0 border-b" style={{ borderColor: 'var(--border)' }}>{extra}</td>
                    </tr>
                  )}
                </Fragment>
              )
            })
          )}
          {baseline?.removed_nodes.map((r) => (
            <tr key={`removed-${r.id}`} style={{ ...removedRowStyle, color: 'var(--muted-foreground)' }}>
              <td
                className="px-3 py-2 border-b"
                style={{ borderColor: 'var(--border)', paddingLeft: 12 + r.depth * 16, color: 'var(--foreground)' }}
              >
                <RowSign kind="removed" />
                {r.name}
              </td>
              <td className={cellClass} style={{ borderColor: 'var(--border)' }}>—</td>
              <td className={cellClass} style={{ borderColor: 'var(--border)' }}>—</td>
              {periodLabels && <td className={cellClass} style={{ borderColor: 'var(--border)' }}>—</td>}
              <td className={cellClass} style={{ borderColor: 'var(--border)' }}>—</td>
              <td className={cellClass} style={{ borderColor: 'var(--border)', color: 'var(--foreground)' }}>
                {formatRevenueFull(r.target_revenue, currency)}
              </td>
              {periodLabels && <td className={cellClass} style={{ borderColor: 'var(--border)' }}>—</td>}
              <td className={cellClass} style={{ borderColor: 'var(--border)' }}>—</td>
              {!periodLabels && <td className={cellClass} style={{ borderColor: 'var(--border)' }}>—</td>}
            </tr>
          ))}
          <tr style={{ borderTop: '2px solid var(--border)' }}>
            <td className="px-3 py-2 font-semibold" style={{ color: 'var(--foreground)' }}>
              Summary
            </td>
            <td className="px-3 py-2 text-right tabular-nums font-semibold" style={{ color: 'var(--foreground)' }}>
              {formatRevenueFull(pastTotal, currency)}
            </td>
            <td />
            {periodLabels && (
              <td className="px-3 py-2 text-right tabular-nums font-semibold" style={{ color: 'var(--foreground)' }}>
                {formatRevenueFull(actualTotal, currency)}
              </td>
            )}
            <td className="px-3 py-2 text-right tabular-nums font-semibold" style={{ color: 'var(--foreground)' }}>
              {pastTotal > 0 ? `${Math.round(((actualTotal + total - pastTotal) / pastTotal) * 100)}%` : '—'}
            </td>
            {summaryTargetCell}
            {summaryFullYearCell}
            <td />
            {!periodLabels && (
              <td className="px-3 py-2 text-right tabular-nums font-semibold" style={{ color: 'var(--foreground)' }}>
                {formatRevenueFull(ytdTotal, currency)}
                <span style={{ color: 'var(--muted-foreground)' }}>
                  {' · '}{total > 0 ? formatPct(ytdTotal / total) : '—'}
                </span>
              </td>
            )}
          </tr>
        </tbody>
      </table>
    </div>
  )
}
